import { useState, useMemo } from 'react';
import { DataTable } from '../ui/DataTable';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';
import { 
  Check, 
  Eye, 
  Edit2, 
  Trash2, 
  CheckSquare, 
  MoreVertical, 
  FileText, 
  Download,
  Upload,
  Paperclip,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  Send,
  ThumbsUp,
  ThumbsDown,
  BookCheck,
  XCircle,
  X
} from 'lucide-react';
import { JOURNAL_TYPES } from '../../config/accounting';
import type { JournalEntry } from '../../types/accounting';
import { useCurrency } from '../../hooks/useCurrency';

interface UnifiedJournalListProps {
  entries: JournalEntry[];           // Entrées déjà paginées et filtrées (inclut agent si applicable)
  agentEntries: JournalEntry[];      // Liste complète des entrées agent (pour la sélection/validation en lot)
  onView: (entry: JournalEntry) => void;
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
  onValidate?: (entry: JournalEntry) => void;
  onValidateAll?: (entries: JournalEntry[]) => void;
  onSubmitEntry?: (entry: JournalEntry) => void;
  onApproveEntry?: (entry: JournalEntry) => void;
  onRejectEntry?: (entry: JournalEntry, reason: string) => void;
  onPostEntry?: (entry: JournalEntry) => void;
  onRejectAgentEntry?: (entry: JournalEntry, reason: string) => void;
  onBatchSubmit?: (ids: string[]) => void;
  onBatchApprove?: (ids: string[]) => void;
  onBatchPost?: (ids: string[]) => void;
  getAvailableActions?: (entry: JournalEntry) => string[];
  onExport: (format: 'pdf' | 'excel' | 'csv') => void;
  onRefresh?: () => Promise<void>;
  onUploadAttachment?: (entry: JournalEntry, file: File) => void;
  onDownloadAttachment?: (entry: JournalEntry, attachmentId: string) => void;
  onReverse?: (entry: JournalEntry) => void;
  loading?: boolean;
  page: number;
  totalPages: number;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function UnifiedJournalList({
  entries,           // Entrées déjà paginées - NE PAS re-combiner
  agentEntries,      // Utilisé seulement pour la sélection en lot
  onView,
  onEdit,
  onDelete,
  onValidate,
  onValidateAll,
  onSubmitEntry,
  onApproveEntry,
  onRejectEntry,
  onPostEntry,
  onRejectAgentEntry,
  onBatchSubmit,
  onBatchApprove,
  onBatchPost,
  getAvailableActions,
  onExport,
  onRefresh,
  onUploadAttachment,
  onDownloadAttachment,
  onReverse,
  loading,
  page,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange
}: UnifiedJournalListProps) {
  const [selectedEntries, setSelectedEntries] = useState<JournalEntry[]>([]);
  const [selectedJournalEntries, setSelectedJournalEntries] = useState<JournalEntry[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [rejectDialogEntry, setRejectDialogEntry] = useState<JournalEntry | null>(null);
  const [rejectDialogType, setRejectDialogType] = useState<'journal' | 'agent'>('journal');
  const [rejectReason, setRejectReason] = useState('');

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  const openRejectDialog = (entry: JournalEntry, type: 'journal' | 'agent') => {
    setRejectDialogEntry(entry);
    setRejectDialogType(type);
    setRejectReason('');
  };

  const handleRejectConfirm = () => {
    if (!rejectDialogEntry || !rejectReason.trim()) return;
    if (rejectDialogType === 'journal' && onRejectEntry) {
      onRejectEntry(rejectDialogEntry, rejectReason.trim());
    } else if (rejectDialogType === 'agent' && onRejectAgentEntry) {
      onRejectAgentEntry(rejectDialogEntry, rejectReason.trim());
    }
    setRejectDialogEntry(null);
    setRejectReason('');
  };

  const toggleSelectJournalEntry = (entry: JournalEntry) => {
    if (selectedJournalEntries.find(e => e.id === entry.id)) {
      setSelectedJournalEntries(selectedJournalEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedJournalEntries([...selectedJournalEntries, entry]);
    }
  };
  
  // Les entrées sont déjà filtrées et paginées par le parent (JournalsPage)
  // Ne PAS re-combiner pour éviter les doublons
  const paginatedEntries = entries;

  // Compter les écritures sélectionnables par statut
  const selectableByStatus = useMemo(() => {
    const draft = paginatedEntries.filter(e => e.source !== 'agent' && e.status === 'draft');
    const pending = paginatedEntries.filter(e => e.source !== 'agent' && e.status === 'pending');
    const approved = paginatedEntries.filter(e => e.source !== 'agent' && e.status === 'approved');
    return { draft, pending, approved };
  }, [paginatedEntries]);
  
  const isAllSelected = agentEntries.length > 0 && selectedEntries.length === agentEntries.length;
  const { formatConverted } = useCurrency();
  
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries([...agentEntries]);
    }
  };

  const toggleSelectEntry = (entry: JournalEntry) => {
    if (selectedEntries.find(e => e.id === entry.id)) {
      setSelectedEntries(selectedEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
    }
  };

  const getRowClassName = (entry: JournalEntry) => {
    if (entry.source === 'agent' && entry.validationStatus === 'pending') {
      return 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30';
    }
    
    if (entry.status === 'posted') {
      return 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800/70';
    }
    
    if (entry.status === 'approved') {
      return 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30';
    }

    if (entry.status === 'pending') {
      return 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30';
    }
    
    return '';
  };

  const getStatusBadge = (status: JournalEntry['status']) => {
    const statusConfig = {
      draft: { label: 'Brouillon', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
      pending: { label: 'En attente', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' },
      approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' },
      posted: { label: 'Comptabilisé', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' },
      rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' },
      cancelled: { label: 'Annulé', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const columns = [
    {
      header: 'Date',
      accessor: (entry: JournalEntry) => new Date(entry.date).toLocaleDateString('fr-FR')
    },
    {
      header: 'N° Pièce',
      accessor: (entry: JournalEntry) => entry.reference
    },
    {
      header: 'Journal',
      accessor: (entry: JournalEntry) => {
        // Mapping between journalType and JOURNAL_TYPES constant keys
        const journalTypeMapping: { [key: string]: keyof typeof JOURNAL_TYPES } = {
          'sales': 'SALES',
          'purchases': 'PURCHASE',
          'bank': 'BANK',
          'cash': 'CASH',
          'general': 'GENERAL',
          'inventory': 'GENERAL',
          'financial': 'GENERAL',
          'miscellaneous': 'GENERAL'
        };
        
        const mappedType = journalTypeMapping[entry.journalType];
        return mappedType ? JOURNAL_TYPES[mappedType].label : entry.journalType;
      }
    },
    {
      header: 'Unité',
      accessor: (entry: JournalEntry) => entry.businessUnitName || '—',
    },
    {
      header: 'Comptes',
      accessor: (entry: JournalEntry) => {
        // Protection contre lines undefined ou non-array
        const safeLines = Array.isArray(entry.lines) ? entry.lines : [];
        return (
          <div className="text-sm">
            {safeLines.map((line, index) => (
              <div key={line.id || `line-${index}`} className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {line.accountCode || '-'}
                </span>
                <span className="text-gray-600 dark:text-gray-400 text-xs break-words" title={line.accountName || '-'}>
                  {line.accountName || '-'}
                </span>
              </div>
            ))}
            {safeLines.length === 0 && (
              <span className="text-gray-400 italic text-xs">Aucune ligne</span>
            )}
          </div>
        );
      }
    },
    {
      header: 'Libellé',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center">
          {entry.source === 'agent' && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 mr-2">
              ADHA
              {entry.confidence != null && (
                <span className="ml-1 opacity-75">{Math.round(entry.confidence)}%</span>
              )}
            </span>
          )}
          <span className="break-words" title={entry.description}>{entry.description}</span>
        </div>
      )
    },
    {
      header: 'Débit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalDebit),
      className: 'text-right'
    },
    {
      header: 'Crédit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalCredit),
      className: 'text-right'
    },
    {
      header: 'Statut',
      accessor: (entry: JournalEntry) => (
        <div className="flex flex-col gap-1">
          {getStatusBadge(entry.status)}
          {entry.reversalOf && (
            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 text-xs font-medium">
              <RotateCcw className="h-3 w-3 mr-1" />
              Extourne
            </span>
          )}
          {entry.reversedBy && (
            <span className="inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 px-2 py-0.5 text-xs font-medium">
              Extourné
            </span>
          )}
          {entry.smtWarnings && entry.smtWarnings.length > 0 && (
            <span
              className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 px-2 py-0.5 text-xs font-medium cursor-help"
              title={entry.smtWarnings.join('\n')}
            >
              ⚠ SMT ({entry.smtWarnings.length})
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Pièces',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center gap-1.5">
          {entry.attachments && entry.attachments.length > 0 ? (
            <div className="flex items-center gap-1">
              {/* Mini-thumbnails pour les images, icônes pour les docs */}
              {entry.attachments.slice(0, 3).map((att) => (
                <button
                  key={att.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (att.url) window.open(att.url, '_blank');
                    else if (att.localUrl) window.open(att.localUrl, '_blank');
                  }}
                  className="flex-shrink-0 rounded overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all cursor-pointer"
                  title={att.name}
                >
                  {att.mimeType?.startsWith('image/') && (att.url || att.localUrl) ? (
                    <img src={att.url || att.localUrl} alt={att.name} className="h-7 w-7 object-cover" />
                  ) : (
                    <div className="h-7 w-7 flex items-center justify-center bg-gray-50">
                      <FileText className="h-3.5 w-3.5 text-blue-500" />
                    </div>
                  )}
                </button>
              ))}
              {entry.attachments.length > 3 && (
                <span className="text-xs text-gray-500 ml-0.5">+{entry.attachments.length - 3}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: (entry: JournalEntry) => {
        const isPosted = entry.status === 'posted';
        const isAgentPending = entry.source === 'agent' && entry.validationStatus === 'pending';
        const actions = getAvailableActions ? getAvailableActions(entry) : [];
        
        const menuItems = [
          {
            label: 'Voir les détails',
            icon: Eye,
            onClick: () => onView(entry)
          },
          // === Workflow status actions ===
          ...(actions.includes('submit') && onSubmitEntry ? [{
            label: 'Soumettre pour approbation',
            icon: Send,
            onClick: () => onSubmitEntry(entry)
          }] : []),
          ...(actions.includes('validateAndPost') && onSubmitEntry ? [{
            label: 'Valider et comptabiliser',
            icon: BookCheck,
            onClick: () => onSubmitEntry(entry) // Will be handled as validateAndPost in parent
          }] : []),
          ...(actions.includes('approve') && onApproveEntry ? [{
            label: 'Approuver',
            icon: ThumbsUp,
            onClick: () => onApproveEntry(entry)
          }] : []),
          ...(actions.includes('reject') && onRejectEntry ? [{
            label: 'Rejeter',
            icon: ThumbsDown,
            onClick: () => openRejectDialog(entry, 'journal'),
            variant: 'danger' as const
          }] : []),
          ...(actions.includes('post') && onPostEntry ? [{
            label: 'Comptabiliser',
            icon: BookCheck,
            onClick: () => onPostEntry(entry)
          }] : []),
          // === Attachments ===
          ...(entry.attachments && entry.attachments.length > 0 ? [{
            label: 'Télécharger pièces',
            icon: Download,
            onClick: () => entry.attachments?.forEach(att => 
              onDownloadAttachment && onDownloadAttachment(entry, att.id)
            )
          }] : []),
          ...(!isPosted && onUploadAttachment ? [{
            label: 'Ajouter pièce',
            icon: Upload,
            onClick: () => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf,.jpg,.jpeg,.png';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) onUploadAttachment(entry, file);
              };
              input.click();
            }
          }] : []),
          // === Edit ===
          ...(!isPosted && actions.includes('edit') ? [{
            label: 'Modifier',
            icon: Edit2,
            onClick: () => onEdit(entry)
          }] : []),
          // === Agent entries ===
          ...(isAgentPending && onValidate ? [{
            label: 'Valider (agent)',
            icon: Check,
            onClick: () => onValidate(entry)
          }] : []),
          ...(isAgentPending && onRejectAgentEntry ? [{
            label: 'Rejeter (agent)',
            icon: XCircle,
            onClick: () => openRejectDialog(entry, 'agent'),
            variant: 'danger' as const
          }] : []),
          // === Reverse ===
          ...(isPosted && onReverse && !entry.reversedBy ? [{
            label: 'Extourner',
            icon: RotateCcw,
            onClick: () => onReverse(entry)
          }] : []),
          // === Delete ===
          ...(actions.includes('delete') ? [{
            label: 'Supprimer',
            icon: Trash2,
            onClick: () => onDelete(entry),
            variant: 'danger' as const
          }] : [])
        ];

        return (
          <DropdownMenu
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={MoreVertical}
                className="h-8 w-8 p-0"
              />
            }
            items={menuItems}
          />
        );
      }
    }
  ];
  // Ajouter une colonne de sélection pour agent entries ET journal entries
  const hasBatchActions = agentEntries.length > 0 || onBatchSubmit || onBatchApprove || onBatchPost;
  if (hasBatchActions) {
    columns.unshift({
      header: 'Sélection',
      accessor: (entry: JournalEntry) => {
        const isAgentPending = entry.source === 'agent' && entry.validationStatus === 'pending';
        const isSelectableJournal = entry.source !== 'agent' && ['draft', 'pending', 'approved'].includes(entry.status);
        
        if (isAgentPending) {
          return (
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={Boolean(selectedEntries.find(e => e.id === entry.id))}
                onChange={() => toggleSelectEntry(entry)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>
          );
        }
        if (isSelectableJournal) {
          return (
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={Boolean(selectedJournalEntries.find(e => e.id === entry.id))}
                onChange={() => toggleSelectJournalEntry(entry)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>
          );
        }
        return null;
      },
      className: 'w-10'
    } as any);
  }

  // Compter les entrées ADHA dans la liste paginée actuelle
  const agentEntriesInPage = paginatedEntries.filter(e => e.source === 'agent' && e.validationStatus === 'pending').length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2 flex-wrap gap-y-2">
          {/* Bouton de validation en lot des agent entries */}
          {selectedEntries.length > 0 && onValidateAll && (
            <Button
              variant="primary"
              size="sm"
              icon={CheckSquare}
              onClick={() => onValidateAll(selectedEntries)}
            >
              Valider ADHA ({selectedEntries.length})
            </Button>
          )}
          {/* Boutons batch pour les journal entries sélectionnées */}
          {selectedJournalEntries.length > 0 && (
            <>
              {onBatchSubmit && selectedJournalEntries.some(e => e.status === 'draft') && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Send}
                  onClick={() => {
                    const ids = selectedJournalEntries.filter(e => e.status === 'draft').map(e => e.id);
                    onBatchSubmit(ids);
                    setSelectedJournalEntries([]);
                  }}
                >
                  Soumettre ({selectedJournalEntries.filter(e => e.status === 'draft').length})
                </Button>
              )}
              {onBatchApprove && selectedJournalEntries.some(e => e.status === 'pending') && (
                <Button
                  variant="primary"
                  size="sm"
                  icon={ThumbsUp}
                  onClick={() => {
                    const ids = selectedJournalEntries.filter(e => e.status === 'pending').map(e => e.id);
                    onBatchApprove(ids);
                    setSelectedJournalEntries([]);
                  }}
                >
                  Approuver ({selectedJournalEntries.filter(e => e.status === 'pending').length})
                </Button>
              )}
              {onBatchPost && selectedJournalEntries.some(e => e.status === 'approved') && (
                <Button
                  variant="success"
                  size="sm"
                  icon={BookCheck}
                  onClick={() => {
                    const ids = selectedJournalEntries.filter(e => e.status === 'approved').map(e => e.id);
                    onBatchPost(ids);
                    setSelectedJournalEntries([]);
                  }}
                >
                  Comptabiliser ({selectedJournalEntries.filter(e => e.status === 'approved').length})
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                icon={X}
                onClick={() => setSelectedJournalEntries([])}
              >
                Désélectionner
              </Button>
            </>
          )}
          {/* Info sur les entrées ADHA à valider */}
          {agentEntriesInPage > 0 && selectedEntries.length === 0 && selectedJournalEntries.length === 0 && (
            <span className="text-sm text-orange-600 dark:text-orange-400">
              {agentEntriesInPage} écriture{agentEntriesInPage > 1 ? 's' : ''} ADHA en attente de validation
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Raccourcis sélection rapide */}
          {(selectableByStatus.draft.length > 0 || selectableByStatus.pending.length > 0 || selectableByStatus.approved.length > 0) && (
            <DropdownMenu
              trigger={
                <Button variant="outline" size="sm" icon={CheckSquare}>
                  <span className="hidden sm:inline">Sélectionner par statut</span>
                </Button>
              }
              items={[
                ...(selectableByStatus.draft.length > 0 ? [{
                  label: `Brouillons (${selectableByStatus.draft.length})`,
                  icon: FileText,
                  onClick: () => setSelectedJournalEntries(selectableByStatus.draft)
                }] : []),
                ...(selectableByStatus.pending.length > 0 ? [{
                  label: `En attente (${selectableByStatus.pending.length})`,
                  icon: Send,
                  onClick: () => setSelectedJournalEntries(selectableByStatus.pending)
                }] : []),
                ...(selectableByStatus.approved.length > 0 ? [{
                  label: `Approuvés (${selectableByStatus.approved.length})`,
                  icon: ThumbsUp,
                  onClick: () => setSelectedJournalEntries(selectableByStatus.approved)
                }] : []),
              ]}
            />
          )}
          <span className="text-sm text-gray-500">
            {paginatedEntries.length} écriture{paginatedEntries.length > 1 ? 's' : ''} affichée{paginatedEntries.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Sélection en lot des entrées ADHA si il y en a */}
      {agentEntriesInPage > 0 && (
        <div className="flex items-center mb-2 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            className="mr-2 rounded border-gray-300 text-primary focus:ring-primary"
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm text-orange-800 dark:text-orange-200">
            Sélectionner toutes les écritures ADHA en attente ({agentEntries.filter(e => e.validationStatus === 'pending').length})
          </label>
        </div>
      )}

      <DataTable
        columns={columns}
        data={paginatedEntries}
        loading={loading}
        emptyMessage="Aucune écriture trouvée. Utilisez les filtres pour affiner votre recherche ou créez une nouvelle écriture."
        emptyTitle="Aucune écriture comptable"
        emptyAction={{
          label: "Créer une écriture",
          onClick: () => console.log("Créer une nouvelle écriture"),
          icon: 'plus'
        }}
        rowClassName={getRowClassName}
        showToolbar={true}
        showPagination={true}
        enableResize={true}
        enableSort={true}
        enableFilters={true}
        enableColumnToggle={true}
        enableGlobalFilter={true}
        storageKey="unified-journal-list"
        maxHeight="calc(100vh - 400px)"
        density="normal"
        // Pagination externe
        page={page}
        totalPages={Math.max(1, totalPages)}
        totalItems={totalItems ?? (paginatedEntries.length * totalPages)}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        toolbarActions={
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing || loading}
                icon={RefreshCw}
                className={isRefreshing ? 'animate-spin-icon' : ''}
              >
                <span className="hidden sm:inline">{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
              </Button>
            )}
            <DropdownMenu
              trigger={
                <Button variant="secondary" size="sm" icon={Download}>
                  <span className="hidden sm:inline">Exporter</span>
                </Button>
              }
              items={[
                {
                  label: 'Export PDF',
                  icon: FileText,
                  onClick: () => onExport('pdf')
                },
                {
                  label: 'Export Excel',
                  icon: FileText,
                  onClick: () => onExport('excel')
                },
                {
                  label: 'Export CSV',
                  icon: Download,
                  onClick: () => onExport('csv')
                }
              ]}
            />
          </div>
        }
      />

      {/* Dialogue de rejet (journal ou agent entry) */}
      {rejectDialogEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {rejectDialogType === 'agent' ? 'Rejeter l\'écriture ADHA' : 'Rejeter l\'écriture'}
            </h3>
            <p className="text-sm text-muted-foreground mb-1">
              {rejectDialogEntry.reference} — {rejectDialogEntry.description}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Veuillez indiquer le motif du rejet :
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Motif du rejet..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-3 text-sm bg-white dark:bg-gray-700 text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setRejectDialogEntry(null); setRejectReason(''); }}
              >
                Annuler
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={ThumbsDown}
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
              >
                Rejeter
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
